import {useAutocompleteTriggersAndSuggestions} from '@github-ui/inline-autocomplete/hooks/use-autocomplete-triggers-and-suggestions'
import type {SuggestionOptions} from '@github-ui/markdown-editor/suggestions'
import {type Emoji, useEmojiSuggestions} from '@github-ui/markdown-editor/suggestions/use-emoji-suggestions'
import {type Mentionable, useMentionSuggestions} from '@github-ui/markdown-editor/suggestions/use-mention-suggestions'
import {type Reference, useReferenceSuggestions} from '@github-ui/markdown-editor/suggestions/use-reference-suggestions'
import {useMemo} from 'react'

export const useStandardAutocompleteConfig = (
  referenceSuggestions?: SuggestionOptions<Reference>,
  mentionSuggestions?: SuggestionOptions<Mentionable>,
  emojiSuggestions?: SuggestionOptions<Emoji>,
  emojiTone?: number,
) => {
  const emojiConfig = useMemo(() => ({tone: emojiTone}), [emojiTone])
  const emptyArray: [] = [] // constant reference to avoid re-running effects
  const {trigger: emojiTrigger, calculateSuggestions: calculateEmojiSuggestions} = useEmojiSuggestions(
    emojiSuggestions ?? emptyArray,
    emojiConfig,
  )
  const {trigger: mentionsTrigger, calculateSuggestions: calculateMentionSuggestions} = useMentionSuggestions(
    mentionSuggestions ?? emptyArray,
  )
  const {trigger: referencesTrigger, calculateSuggestions: calculateReferenceSuggestions} = useReferenceSuggestions(
    referenceSuggestions ?? emptyArray,
  )
  const triggersAndSuggestions = useMemo(
    () => [
      {trigger: emojiTrigger, suggestionsCalculator: calculateEmojiSuggestions},
      {
        trigger: mentionsTrigger,
        suggestionsCalculator: calculateMentionSuggestions,
      },
      {
        trigger: referencesTrigger,
        suggestionsCalculator: calculateReferenceSuggestions,
      },
    ],
    [
      calculateEmojiSuggestions,
      calculateMentionSuggestions,
      calculateReferenceSuggestions,
      emojiTrigger,
      mentionsTrigger,
      referencesTrigger,
    ],
  )
  return useAutocompleteTriggersAndSuggestions(triggersAndSuggestions)
}
